import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IOrder } from 'shared/interfaces/order.interface';
import { IState } from 'shared/interfaces/state.interface';
import { IOrdersGetData, TGetOneEntityAction } from 'shared/types';
import { initialStore } from '../initial-store';
import { IQuestionnaireInstance } from 'shared/interfaces/questionnaire-instance.interface';

// Actions
export const createOrderNote = createAction(
  'orders/CREATE_ORDER_NOTE',
  (payload) => ({ payload })
);

export const deleteOrderNote = createAction(
  'orders/DELETE_ORDER_NOTE',
  (payload) => ({ payload })
);

export const leaveWaitingRoom = createAction(
  'orders/LEAVE_WAITING_ROOM',
  (payload) => ({ payload })
);
export const cancelOrder = createAction('orders/CANCEL_ORDER', (payload) => ({
  payload
}));

export const createJiraServiceTicket = createAction(
  'orders/CREATE_JIRA_SERVICE_TICKET',
  (payload) => ({ payload })
);

export const updateOrderStatusAction = createAction(
  'orders/UPDATE_ORDER_STATUS',
  (payload) => ({ payload })
);

export const elevateVisitType = createAction(
  'visits/ELEVATE_VISIT_TYPE',
  (payload) => ({ payload })
);

export const assignAffiliateAdmin = createAction(
  'orders/ASSIGN_AFFILIATE_ADMIN',
  (payload) => ({ payload })
);

export const updatePrerequisiteInstance = createAction(
  'prerequisite/updatePrerequisiteInstance',
  (payload) => ({
    payload
  })
);

export const uploadFilePrerequisiteInstance = createAction(
  'prerequisite/uploadFilePrerequisiteInstance',
  (payload) => ({
    payload
  })
);

export const sendToPharmacyRecipients = createAction(
  'orders/sendToPharmacyRecipients',
  (payload) => ({
    payload
  })
);

// Slice
const ordersSlice = createSlice({
  name: 'orders',
  initialState: initialStore.orders,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getOrdersData: (state, data: PayloadAction<IOrdersGetData | undefined>) => {
      if (!state.isLoading) {
        state.isLoading = true;
      }
      if (data?.payload?.currentTab) {
        state.currentTab = data?.payload?.currentTab;
      }
    },
    setOrdersData: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload.result;
      state.countOrders = payload.count;
    },
    setOrdersReportData: (state, { payload }) => {
      state.isLoading = false;
      state.dataReport = payload.result;
    },
    clearReportData: (state) => {
      state.dataReport = [];
    },
    getOrdersFail: (state) => {
      state.isLoading = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getOrder: (state, data: PayloadAction<TGetOneEntityAction>) => {
      state.isLoading = true;
    },
    setOrder: (state, { payload }) => {
      state.isLoading = false;
      state.currentOrder = payload;
    },
    setSingleOrderData: (state, { payload }) => {
      if (state.currentOrder.data) {
        state.currentOrder.data = payload;
      }
    },
    updateOrder: (state, { payload }) => {
      state.isLoading = false;
      state.currentOrder.data = payload.order;
      state.currentOrder.visits = payload.visits;
    },
    addNewNote: (state, { payload }) => {
      state.currentOrder.notes = state.currentOrder.notes.concat(payload);
    },
    removeNote: (state, { payload }) => {
      state.currentOrder.notes = state.currentOrder.notes.filter(
        ({ id }) => id !== payload
      );
    },
    addVisits: (state, { payload }) => {
      state.currentOrder.visits = payload;
    },
    updateOrderStatus: (state, { payload }) => {
      if (state.currentOrder.data) {
        const isPreviousStatusMatchingTab =
          (state.currentOrder.data as IOrder).status === state.currentTab;
        const isPayloadStatusMatchingCurrentTab =
          payload.status === state.currentTab;

        if (isPreviousStatusMatchingTab) {
          state.countOrders = state.countOrders - 1;
        }
        if (isPayloadStatusMatchingCurrentTab) {
          state.countOrders = state.countOrders + 1;
        }
        (state.currentOrder.data as IOrder).status = payload.status;
        (state.currentOrder.data as IOrder).history = payload.history;
      }
      if (state.data) {
        (state.data as IOrder[]) = state.data.map((el: IOrder) =>
          el.id === payload.id ? payload : el
        );
      }
      if (state.currentTab !== 'all') {
        (state.data as IOrder[]) = state.data.filter(
          (order: IOrder) => order?.status === state.currentTab
        );
      }
    },
    expediteOrderData: (state) => {
      if (!state.isLoading) {
        state.isLoading = true;
      }
    },
    expediteOrder: (state, { payload }) => {
      if (state.currentOrder.data) {
        (state.currentOrder.data as IOrder).expedited = payload.expedited;
        (state.currentOrder.data as IOrder).history = payload.history;
      }
      state.isLoading = false;
    },
    clearOrderData: (state) => {
      if (state.currentOrder) {
        state.currentOrder.data = null;
        state.currentOrder.paymentMethod = null;
        state.currentOrder.notes = [];
        state.currentOrder.visits = [];
      }
      state.isLoading = false;
    },
    updateCurrentOrder: (state, { payload }) => {
      state.isLoading = false;
      state.currentOrder.data = payload;
    },
    assignAffiliateAdminAction: (state, { payload }) => {
      if (state.currentOrder.data) {
        (state.currentOrder.data as IOrder).assignedAffiliateAdmin =
          payload.assignedAffiliateAdmin;
        (state.currentOrder.data as IOrder).history = payload.history;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    submitOrder: (state, { payload }) => {
      state.isLoading = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    submitOrderSuccess: (state, { payload }) => {
      state.isLoading = false;
    },
    submitOrderFail: (state) => {
      state.isLoading = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateDataOrder: (state, { payload }) => {
      state.isLoading = true;
    },
    updateDataOrderFail: (state) => {
      state.isLoading = false;
    },
    setPrerequisiteInstance: (state, { payload }) => {
      if (state.currentOrder.data) {
        const updatedPrerequisiteInstances = (
          state.currentOrder.data as IOrder
        ).prerequisiteInstances.map((prerequisiteInstance) => {
          return prerequisiteInstance.id === payload.id
            ? payload
            : prerequisiteInstance;
        });
        (state.currentOrder.data as IOrder).prerequisiteInstances = [
          ...updatedPrerequisiteInstances
        ];
      }
    },
    setFilePrerequisiteInstance: (state, { payload }) => {
      if (state.currentOrder.data) {
        const updatedPrerequisiteInstances = (
          state.currentOrder.data as IOrder
        ).prerequisiteInstances.map((prerequisiteInstance) => {
          if (prerequisiteInstance.id === payload.id) {
            return {
              ...prerequisiteInstance,
              files: payload.files
            };
          }
          return prerequisiteInstance;
        });
        (state.currentOrder.data as IOrder).prerequisiteInstances = [
          ...updatedPrerequisiteInstances
        ];
      }
    },
    updateOrderQuestionnaireInstances: (state, { payload }) => {
      if (state.currentOrder.data) {
        const order = state.currentOrder.data as IOrder;
        const existingQuestionnaire = (
          order.questionnaireInstances as IQuestionnaireInstance[]
        ).find((qi) => qi.id === payload.id);
        if (order.questionnaireInstances && !existingQuestionnaire)
          order.questionnaireInstances = [
            ...order.questionnaireInstances,
            payload
          ];
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getPharmacyUpdate: (state, { payload }) => {
      state.isLoading = true;
    },
    setExternalPharmacy: (state, { payload }) => {
      if (state.currentOrder.data) {
        (state.currentOrder.data as IOrder).externalPharmacy =
          payload.dosespotPharmacy;
        (state.currentOrder.data as IOrder).externalPharmacyId =
          payload.dosespotPharmacyId;
      }
    },
    sendOrderLink: (state, _data) => {
      state.isLoading = true;
    },
    approveOrder: (state, _data) => {
      state.isLoading = true;
    },
    approveOrderSuccess: (state, data) => {
      state.isLoading = false;
      if (state.currentOrder?.data) {
        state.currentOrder.data = data.payload;
        const index = state.data.findIndex(
          (order: IOrder) => order.id === data.payload.id
        );
        if (index !== -1) {
          (state.data[index] as IOrder) = data.payload;
        }
      }
    },
    approveOrderFail: (state, _data) => {
      state.isLoading = false;
    },
    setOrderLabOrders: (state, { payload }) => {
      if (state.currentOrder?.data) {
        (state.currentOrder.data as IOrder).siblingLabOrders = [
          ...(state.currentOrder.data as IOrder).siblingLabOrders,
          payload
        ];
      }
    }
  }
});

export const {
  getOrdersData,
  setOrdersData,
  getOrdersFail,
  setOrdersReportData,
  clearReportData,
  getOrder,
  setOrder,
  setSingleOrderData,
  updateOrder,
  addNewNote,
  removeNote,
  addVisits,
  updateOrderStatus,
  expediteOrder,
  expediteOrderData,
  clearOrderData,
  updateCurrentOrder,
  assignAffiliateAdminAction,
  submitOrder,
  submitOrderSuccess,
  submitOrderFail,
  updateDataOrder,
  updateDataOrderFail,
  setPrerequisiteInstance,
  setFilePrerequisiteInstance,
  updateOrderQuestionnaireInstances,
  getPharmacyUpdate,
  setExternalPharmacy,
  sendOrderLink,
  setOrderLabOrders,
  approveOrder,
  approveOrderSuccess,
  approveOrderFail
} = ordersSlice.actions;

export const { reducer } = ordersSlice;

// Selectors
export const getOrdersSelector = (state: IState) => state && state.orders.data;

export const getOrdersReportSelector = (state: IState) =>
  state && state.orders.dataReport;

export const getOrdersCurrentTabSelector = (state: IState) =>
  state && state.orders.currentTab;

export const getOrdersCountSelector = (state: IState) =>
  state && state.orders.countOrders;

export const getOrdersLoadingSelector = (state: IState) =>
  state && state.orders.isLoading;

export const getCurrentOrderSelector = (state: IState) =>
  state && {
    order: state.orders.currentOrder.data,
    paymentMethod: state.orders.currentOrder.paymentMethod,
    notes: state.orders.currentOrder.notes,
    visits: state.orders.currentOrder.visits
  };
